import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import OnInfoIcon from '../../../icons/OnInfoIcon';
import { clearValidationErrors, createDar } from '../../../store/participantService';
import { getObjFromId } from '../../../utils/directusFunctions';
import { MAX_PTCODE } from '../../../utils/formValidation/commonConstants';
import { clearKeys } from '../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextAreaField from '../../formElements/TextAreaField';
import TextInputField from '../../formElements/TextInputField';
import FormActions from '../../ui/formActions/FormActions';

import { validate } from './darValidation/validateDar';
import DarDocumentUploader from './DarDocumentUploader';
import { PRIORITY_INFO } from './priorityInfo';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const _NOTES_LIMIT = 750;
export const CREATE_DAR_SUCCESS_MESSAGE = 'DAR has been added';

const CreateDar = ({ onClose, roles, darRequestTypes, darPriorities }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const { state } = useLocation();
    const initialState = {
        ptCode: '',
        requestTypeId: '',
        priorityId: '',
        notes: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isPreUploadErrors, setIsPreUploadErrors] = useState(false);
    const [keys, setKeys] = useState({ requestTypeId: '0', priorityId: '1' });
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [uploadDocuments, setUploadDocuments] = useState(false);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const validationErrors = useSelector(
        (state) => state.entities.participantService.validationErrors
    );

    // USE EFFECTS
    useEffect(() => {
        const isError = Object.values(errors).some((el) => el.error) || isPreUploadErrors;
        setIsButtonDisabled(isError);
    }, [errors, isPreUploadErrors]);

    useEffect(() => {
        if (successMessage === CREATE_DAR_SUCCESS_MESSAGE) {
            if (!uploadDocuments) onFormExit();
        }
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
    }, [successMessage, validationErrors]);

    useEffect(() => {
        if (state?.darAccordion === 'participantValidation' && currentParticipant?.ptCode) {
            setNewEntry((prev) => ({
                ...prev,
                ptCode: currentParticipant.ptCode
            }));
        }
    }, [currentParticipant]);

    // HELPER FNS
    const clearForm = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        setErrors({});
        setUploadDocuments(false);
        setIsButtonDisabled(false);
    };

    const onUploadErrors = () => setNewEntry(initialState);

    const clearError = (key) => {
        // eslint-disable-next-line
        if (key in errors) setErrors((prev) => (({ [key]: _, ...prev }) => prev)(prev));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateUploadDocuments = (option) => setUploadDocuments(option);

    const onFormExit = () => {
        clearForm();
        onClose();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(createDar(newEntry, CREATE_DAR_SUCCESS_MESSAGE));
    };

    // RENDER
    return (
        <div className={form.formWrapper}>
            <form className={form.form} onSubmit={onSubmit} data-testid="create_dar_form">
                <h2 className={app.sectionHeading}>Create Data Amendment Request</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'requestTypeId'}
                            key={keys.requestTypeId}
                            label="Request Type"
                            placeholder="Select request type..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            menuItems={darRequestTypes || []}
                            selectedId={newEntry.requestTypeId}
                            selected={getObjFromId(darRequestTypes, newEntry.requestTypeId)}
                            error={errors.requestTypeId}
                            onChange={(chosenId) => onUpdate('requestTypeId', chosenId)}
                        />
                        {state?.darAccordion !== 'participantValidation' && (
                            <TextInputField
                                id={'ptCode'}
                                label={'PT ID'}
                                mandatory={true}
                                placeholder={'Enter PT code'}
                                maxLength={MAX_PTCODE}
                                value={newEntry.ptCode || ''}
                                error={errors.ptCode}
                                onChange={(e) => onUpdate('ptCode', e.target.value.toUpperCase())}
                            />
                        )}
                    </div>
                    <div className={form.formColumn}>
                        <div className={form.withInfoWrapper} data-testid="priority-info-icon">
                            <SingleSelect
                                id="priorityId"
                                key={keys.priorityId}
                                label="Priority"
                                placeholder="Select priority..."
                                disabled={!hasRole(acceptedRoles, roles)}
                                mandatory={true}
                                menuItems={darPriorities || []}
                                selectedId={newEntry.priorityId}
                                selected={getObjFromId(darPriorities, newEntry.priorityId)}
                                error={errors.priorityId}
                                onChange={(chosenId) => onUpdate('priorityId', chosenId)}
                            />
                            <OnInfoIcon content={PRIORITY_INFO} />
                        </div>
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextAreaField
                            id={'notes'}
                            label={'Notes'}
                            placeholder={'Enter data amendment request notes...'}
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            maxLength={_NOTES_LIMIT}
                            value={newEntry.notes}
                            count={`${newEntry.notes.length}/${_NOTES_LIMIT}`}
                            error={errors.notes}
                            onChange={(e) => onUpdate('notes', e.target.value)}
                        />
                        <RadioButtons
                            id="uploadDocuments"
                            label="Upload Documents with this DAR?"
                            value={uploadDocuments || false}
                            onChange={(option) => onUpdateUploadDocuments(option)}
                        />
                    </div>
                </div>
                {uploadDocuments && (
                    <div className={form.formSection}>
                        <div className={`${app.fadein} ${form.formColumn}`}>
                            <DarDocumentUploader
                                isOpenUploader={uploadDocuments}
                                onUploadErrors={onUploadErrors} // clear form
                                onPreUploadErrors={setIsPreUploadErrors} // disable button
                                onExit={onFormExit}
                            />
                        </div>
                    </div>
                )}

                <FormActions
                    id="createDarButton"
                    onClose={onSubmit}
                    onCancel={onFormExit}
                    btnText="Add Data Amendment Request"
                    disabled={!hasRole(acceptedRoles, roles) || isButtonDisabled}
                />
            </form>
        </div>
    );
};

CreateDar.propTypes = {
    row: PropTypes.object,
    onClose: PropTypes.func,
    roles: PropTypes.arrayOf(PropTypes.string),
    formType: PropTypes.string,
    darRequestTypes: PropTypes.array.isRequired,
    darPriorities: PropTypes.array.isRequired
};

export default CreateDar;
